import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http, { authHttp } from "../api/http";
import User from "../types/user";
import { signOut } from "aws-amplify/auth";
import Company from "../types/company";

interface AuthState {
  status: "idle" | "loading" | "failed";
  loggedInUser?: User;
  profileUser?: User;
  userCompany?: Company;
}

const initialState: AuthState = {
  status: "idle",
};

export const fetchProfile = createAsyncThunk<{
  user: User;
  company: Company;
}>("auth/fetch-profile", async () => {
  const response = await authHttp.get<{
    user: User;
    company: Company;
  }>(`/user/profile`, {
    params: {
      refreshToken: true,
    },
  });

  return response.data;
});

export const updateProfile = createAsyncThunk<User, User>(
  "auth/update-profile",
  async (vm: User) => {
    const response = await authHttp.put<Partial<User>>(`/user/${vm.id}`, vm);
    const userResponse = await http.put<Partial<User>>(
      `/user/${vm.detectorId}`,
      vm,
      {
        params: {
          successToast: true,
        },
      },
    );

    const profile = response.data;
    const user = userResponse.data;

    const newUser = { ...user, ...profile } as User;
    return newUser;
  },
);

export const fetchDbUser = createAsyncThunk<Partial<User>, User>(
  "auth/fetch-dbUser",
  async (user: User) => {
    const response = await http.post<Partial<User>>(`/auth`, user);
    return response.data;
  },
);

export const logout = createAsyncThunk("auth/logout", async () => {
  await authHttp.get(`/auth/logout`).then(async () => {
    const response = await signOut();
    return response;
  });
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearLoggedInUser(state) {
      state.loggedInUser = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.pending, (state) => {
        state.status = "loading";
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.status = "idle";
        state.loggedInUser = undefined;
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.status = "idle";
        state.profileUser = action.payload.user;
        state.userCompany = action.payload.company;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(updateProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.status = "idle";
        state.loggedInUser = action.payload;
      })
      .addCase(updateProfile.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchDbUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDbUser.fulfilled, (state, action) => {
        state.status = "idle";
        const profile = Array.isArray(state.profileUser)
          ? state.profileUser[0]
          : state.profileUser;
        const payload = Array.isArray(action.payload)
          ? action.payload[0]
          : action.payload;

        let newUser: User = { ...profile, ...payload };
        state.loggedInUser = newUser;
      })
      .addCase(fetchDbUser.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { clearLoggedInUser } = authSlice.actions;

export default authSlice.reducer;
